import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { openCalendly } from "../lib/calendly";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_3d59354d-0972-4474-8092-a9560caa3a16/artifacts/salc548w_2.jpg";

export default function Navigation() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const isHome = location.pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Handle in-page anchor scroll after navigating from another page
    useEffect(() => {
        if (isHome && location.hash) {
            const id = location.hash.replace("#", "");
            const el = document.getElementById(id);
            if (el) {
                setTimeout(() => {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 80);
            }
        }
    }, [isHome, location.hash]);

    // Lock body scroll when mobile menu is open
    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [menuOpen]);

    const goToSection = (id) => {
        setMenuOpen(false);
        if (isHome) {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            navigate(`/#${id}`);
        }
    };

    const handleCalendly = () => {
        setMenuOpen(false);
        openCalendly();
    };

    return (
        <>
            <nav
                data-testid="main-nav"
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                    scrolled || menuOpen
                        ? "bg-[#050508]/95 backdrop-blur-xl border-b border-[rgba(163,168,216,0.12)]"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 lg:px-20 py-4">
                    <Link
                        to="/"
                        data-testid="nav-logo"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 group"
                    >
                        <div className="logo-frame w-10 h-10 rounded-sm overflow-hidden flex items-center justify-center">
                            <img
                                src={LOGO_ICON}
                                alt="Stout Road"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <span className="font-heading text-xl md:text-2xl tracking-wide text-white">
                            Stout Road
                        </span>
                    </Link>

                    {/* Desktop nav */}
                    <div className="hidden md:flex items-center gap-10">
                        <button
                            data-testid="nav-services-link"
                            onClick={() => goToSection("services")}
                            className="sr-link text-sm font-medium text-white/80"
                        >
                            Services
                        </button>
                        <button
                            data-testid="nav-approach-link"
                            onClick={() => goToSection("approach")}
                            className="sr-link text-sm font-medium text-white/80"
                        >
                            Approach
                        </button>
                        <Link
                            data-testid="nav-about-link"
                            to="/about"
                            className="sr-link text-sm font-medium text-white/80"
                        >
                            About
                        </Link>
                        <button
                            data-testid="nav-contact-link"
                            onClick={() => goToSection("contact")}
                            className="sr-link text-sm font-medium text-white/80"
                        >
                            Contact
                        </button>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            data-testid="nav-cta-button"
                            onClick={openCalendly}
                            className="btn-primary !py-3 !px-5 hidden sm:inline-flex"
                        >
                            Book a Call
                            <ArrowUpRight size={14} />
                        </button>

                        {/* Mobile menu toggle */}
                        <button
                            data-testid="mobile-menu-toggle"
                            aria-label={menuOpen ? "Close menu" : "Open menu"}
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen((v) => !v)}
                            className="md:hidden w-11 h-11 flex items-center justify-center border border-[rgba(163,168,216,0.25)] text-white hover:border-[#C6F432] hover:text-[#C6F432] transition-colors duration-300 rounded-sm"
                        >
                            {menuOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile menu overlay */}
            <div
                data-testid="mobile-menu"
                className={`md:hidden fixed inset-0 z-40 bg-[#050508]/98 backdrop-blur-xl transition-all duration-400 ${
                    menuOpen
                        ? "opacity-100 pointer-events-auto"
                        : "opacity-0 pointer-events-none"
                }`}
                style={{ paddingTop: "76px" }}
            >
                <div className="h-full overflow-y-auto px-6 py-10 flex flex-col">
                    <div className="flex flex-col gap-1">
                        {[
                            { label: "Services", action: () => goToSection("services") },
                            { label: "Approach", action: () => goToSection("approach") },
                            { label: "About", action: () => { setMenuOpen(false); navigate("/about"); } },
                            { label: "Contact", action: () => goToSection("contact") },
                        ].map((item) => (
                            <button
                                key={item.label}
                                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                                onClick={item.action}
                                className="font-heading text-white text-4xl text-left py-5 border-b border-[rgba(163,168,216,0.1)] hover:text-[#C6F432] transition-colors duration-300"
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <button
                        data-testid="mobile-cta-button"
                        onClick={handleCalendly}
                        className="btn-primary mt-10 justify-center w-full"
                    >
                        Book a 30-min Call
                        <ArrowUpRight size={14} />
                    </button>

                    <p className="mt-auto pt-10 text-white/40 text-xs">
                        © {new Date().getFullYear()} Stout Road
                    </p>
                </div>
            </div>
        </>
    );
}
